import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";

import {
  getAllPostsApi,
  getAllPostsByClientIdApi,
  createPostApi,
  toggleReactionApi,
  addCommentApi,
  getCommentsApi,
  getMostViewedPostsApi,
  getUserFeedPostsApi,
  getUserMostViewedPostsApi,
  getPostByIdApi,
} from "../../api/PostActions";

// ==========================
// STATE TYPE
// ==========================
interface PostState {
  posts: any[];
  clientPosts: any[];
  mostViewedPosts: any[];
  selectedPost: any | null;

  loading: boolean;
  detailLoading: boolean;
  mostViewedLoading: boolean;

  comments: Record<
    number,
    any[]
  >;
}

const initialState: PostState = {
  posts: [],
  clientPosts: [],
  mostViewedPosts: [],
  selectedPost: null,

  loading: true,
  detailLoading: false,
  mostViewedLoading: false,

  comments: {},
};

// ==========================
// 🌍 GLOBAL POSTS
//
// Guest:
// GET /posts/all
//
// Logged-in:
// GET /posts/all?user_id=43
// ==========================
export const fetchPosts =
  createAsyncThunk(
    "posts/fetchPosts",

    async (
      user_id?:
        | number
        | null
    ) => {
      const res =
        await getAllPostsApi(
          user_id
        );

      return res?.data || [];
    }
  );

// ==========================
// 👤 CLIENT POSTS
//
// Guest:
// GET /posts/all/:client_id
//
// Logged-in:
// GET /posts/all/:client_id?user_id=43
// ==========================
export const fetchClientPosts =
  createAsyncThunk(
    "posts/fetchClientPosts",

    async (
      {
        client_id,
        user_id,
      }: {
        client_id: number;
        user_id?: number;
      }
    ) => {
      const res =
        await getAllPostsByClientIdApi(
          client_id,
          user_id
        );

      return res?.data || [];
    }
  );

// ==========================
// 📝 CREATE POST
// ==========================
export const createPost =
  createAsyncThunk(
    "posts/createPost",

    async (
      formData: FormData
    ) => {
      const res =
        await createPostApi(
          formData
        );

      return res;
    }
  );

// ==========================
// ❤️ LIKE TOGGLE
// ==========================
export const toggleLike =
  createAsyncThunk(
    "posts/toggleLike",

    async (
      payload: any
    ) => {
      await toggleReactionApi(
        payload
      );

      return payload;
    }
  );

// ==========================
// 💬 ADD COMMENT
// ==========================
export const addComment =
  createAsyncThunk(
    "posts/addComment",

    async (
      payload: any
    ) => {
      await addCommentApi(
        payload
      );

      return payload;
    }
  );

// ==========================
// 💬 FETCH COMMENTS
// ==========================
export const fetchComments =
  createAsyncThunk(
    "posts/fetchComments",

    async (
      post_id: number
    ) => {
      const res =
        await getCommentsApi(
          post_id
        );

      return {
        post_id,
        comments:
          res?.data || [],
      };
    }
  );

// ==========================
// 🔥 GLOBAL MOST VIEWED POSTS
//
// Guest:
// GET /posts/most-viewed
//
// Logged-in:
// GET /posts/most-viewed?user_id=43
//
// IMPORTANT:
// user_id is viewer identity.
// It is NOT client_id.
// ==========================
export const fetchMostViewedPosts =
  createAsyncThunk(
    "posts/fetchMostViewedPosts",

    async (
      user_id?:
        | number
        | null
    ) => {
      const res =
        await getMostViewedPostsApi(
          user_id
        );

      return res?.data || [];
    }
  );

// ==========================
// 👤 OLD USER FOLLOWED CLUB FEED
// Kept for backward compatibility.
// ==========================
export const fetchUserFeed =
  createAsyncThunk(
    "posts/fetchUserFeed",

    async (
      user_id: number
    ) => {
      const res =
        await getUserFeedPostsApi(
          user_id
        );

      return res?.data || [];
    }
  );

// ==========================
// 🔥 OLD USER MOST VIEWED POSTS
// Kept for backward compatibility.
// ==========================
export const fetchUserMostViewedPosts =
  createAsyncThunk(
    "posts/fetchUserMostViewedPosts",

    async (
      user_id: number
    ) => {
      const res =
        await getUserMostViewedPostsApi(
          user_id
        );

      return res?.data || [];
    }
  );

// ==========================
// 📌 GET POST BY ID
// ==========================
export const fetchPostById =
  createAsyncThunk(
    "posts/fetchPostById",

    async (
      post_id: number
    ) => {
      return await getPostByIdApi(
        post_id
      );
    }
  );

// ==========================
// SLICE
// ==========================
const postSlice =
  createSlice({
    name: "posts",

    initialState,

    reducers: {},

    extraReducers: (
      builder
    ) => {
      builder

        // ==========================
        // CLIENT POSTS
        // ==========================
        .addCase(
          fetchClientPosts.pending,
          (state) => {
            state.loading = true;

            // Clear previous club posts immediately.
            // Prevents posts from the previously opened club
            // from appearing while the next club request is loading.
            state.clientPosts = [];
          }
        )

        .addCase(
          fetchClientPosts.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            // Always replace with the current club response.
            // If backend returns [], the previous club posts
            // must not remain in Redux.
            state.clientPosts =
              Array.isArray(
                action.payload
              )
                ? action.payload
                : [];
          }
        )

        .addCase(
          fetchClientPosts.rejected,
          (state) => {
            state.loading = false;

            // Never keep stale posts when the current
            // club request fails.
            state.clientPosts = [];
          }
        )

        // ==========================
        // GLOBAL POSTS
        // ==========================
        .addCase(
          fetchPosts.pending,
          (state) => {
            state.loading = true;
          }
        )

        .addCase(
          fetchPosts.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.posts =
              action.payload || [];
          }
        )

        .addCase(
          fetchPosts.rejected,
          (state) => {
            state.loading = false;
          }
        )

        // ==========================
        // CREATE POST
        // ==========================
        .addCase(
          createPost.fulfilled,
          (
            state,
            action
          ) => {
            if (
              action.payload
                ?.status &&
              action.payload
                ?.data
            ) {
              state.posts.unshift(
                action.payload
                  .data
              );
            }
          }
        )

        // ==========================
        // LIKE
        // ==========================
        .addCase(
          toggleLike.fulfilled,
          (
            state,
            action
          ) => {
            const {
              post_id,
            } =
              action.payload;

            const updatePost =
              (
                list: any[]
              ) => {
                const post =
                  list.find(
                    (item) =>
                      Number(
                        item.post_id
                      ) ===
                      Number(
                        post_id
                      )
                  );

                if (!post) {
                  return;
                }

                post.is_liked =
                  !post.is_liked;

                const currentCount =
                  Number(
                    post.like_count ||
                      0
                  );

                post.like_count =
                  post.is_liked
                    ? currentCount +
                      1
                    : Math.max(
                        currentCount -
                          1,
                        0
                      );
              };

            updatePost(
              state.posts
            );

            updatePost(
              state.clientPosts
            );

            updatePost(
              state.mostViewedPosts
            );

            if (
              state
                .selectedPost &&
              Number(
                state
                  .selectedPost
                  .post_id
              ) ===
                Number(
                  post_id
                )
            ) {
              const post =
                state.selectedPost;

              post.is_liked =
                !post.is_liked;

              const currentCount =
                Number(
                  post.like_count ||
                    0
                );

              post.like_count =
                post.is_liked
                  ? currentCount +
                    1
                  : Math.max(
                      currentCount -
                        1,
                      0
                    );
            }
          }
        )

        // ==========================
        // ADD COMMENT
        // ==========================
        .addCase(
          addComment.fulfilled,
          (
            state,
            action
          ) => {
            const {
              post_id,
              comment_text,
              client_id,
            } =
              action.payload;

            if (
              !state.comments[
                post_id
              ]
            ) {
              state.comments[
                post_id
              ] = [];
            }

            state.comments[
              post_id
            ].unshift({
              comment_id:
                Date.now(),

              post_id,
              client_id,
              comment_text,
            });
          }
        )

        // ==========================
        // FETCH COMMENTS
        // ==========================
        .addCase(
          fetchComments.fulfilled,
          (
            state,
            action
          ) => {
            const {
              post_id,
              comments,
            } =
              action.payload;

            state.comments[
              post_id
            ] =
              comments || [];
          }
        )

        // ==========================
        // GLOBAL MOST VIEWED
        // ==========================
        .addCase(
          fetchMostViewedPosts.pending,
          (state) => {
            state.mostViewedLoading =
              true;
          }
        )

        .addCase(
          fetchMostViewedPosts.fulfilled,
          (
            state,
            action
          ) => {
            state.mostViewedLoading =
              false;

            state.mostViewedPosts =
              action.payload || [];
          }
        )

        .addCase(
          fetchMostViewedPosts.rejected,
          (state) => {
            state.mostViewedLoading =
              false;

            state.mostViewedPosts =
              [];
          }
        )

        // ==========================
        // POST DETAILS
        // ==========================
        .addCase(
          fetchPostById.pending,
          (state) => {
            state.detailLoading =
              true;

            state.selectedPost =
              null;
          }
        )

        .addCase(
          fetchPostById.fulfilled,
          (
            state,
            action
          ) => {
            state.detailLoading =
              false;

            state.selectedPost =
              action.payload?.data ||
              null;
          }
        )

        .addCase(
          fetchPostById.rejected,
          (state) => {
            state.detailLoading =
              false;

            state.selectedPost =
              null;
          }
        )

        // ==========================
        // OLD USER FEED
        // ==========================
        .addCase(
          fetchUserFeed.pending,
          (state) => {
            state.loading = true;
          }
        )

        .addCase(
          fetchUserFeed.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.posts =
              action.payload || [];
          }
        )

        .addCase(
          fetchUserFeed.rejected,
          (state) => {
            state.loading = false;
          }
        )

        // ==========================
        // OLD USER MOST VIEWED
        // ==========================
        .addCase(
          fetchUserMostViewedPosts.pending,
          (state) => {
            state.mostViewedLoading =
              true;
          }
        )

        .addCase(
          fetchUserMostViewedPosts.fulfilled,
          (
            state,
            action
          ) => {
            state.mostViewedLoading =
              false;

            state.mostViewedPosts =
              action.payload || [];
          }
        )

        .addCase(
          fetchUserMostViewedPosts.rejected,
          (state) => {
            state.mostViewedLoading =
              false;
          }
        );
    },
  });

export default postSlice.reducer;
